import { App } from '@capacitor/app';

import { getPlatform, isNative } from './capacitor';
import { isRemoteVersionNewer, parseBuildNumber } from './versionCompare';
import { getAppUpdateInfo } from '../services/appUpdates';

const LOG = '[v2-native-gate]';

/** Fallback when Pilot app_updates is unreachable — matches UPI-fix native release. */
export const FALLBACK_MIN_NATIVE_IOS = {
  version: '44.3.0',
  build: 17,
};

/**
 * @typedef {object} NativeUpdatePolicy
 * @property {string|null} minIosVersion
 * @property {number|null} minIosBuild
 * @property {string|null} minAndroidVersion
 * @property {number|null} minAndroidBuild
 * @property {string|null} message
 */

/**
 * @param {object|null|undefined} remote
 * @returns {NativeUpdatePolicy}
 */
export function policyFromAppUpdateDoc(remote) {
  return {
    minIosVersion:
      remote?.minNativeIosVersion != null ? String(remote.minNativeIosVersion).trim() : null,
    minIosBuild: parseBuildNumber(remote?.minNativeIosBuild),
    minAndroidVersion:
      remote?.minNativeAndroidVersion != null ? String(remote.minNativeAndroidVersion).trim() : null,
    minAndroidBuild: parseBuildNumber(remote?.minNativeAndroidBuild),
    message:
      remote?.forceNativeUpdateMessage != null
        ? String(remote.forceNativeUpdateMessage).trim()
        : null,
  };
}

/**
 * @returns {Promise<NativeUpdatePolicy>}
 */
export async function fetchNativeUpdatePolicy() {
  try {
    const remote = await getAppUpdateInfo();
    const policy = policyFromAppUpdateDoc(remote);
    const hasIosRule = Boolean(policy.minIosVersion);
    const hasAndroidRule = Boolean(policy.minAndroidVersion);
    if (hasIosRule || hasAndroidRule) {
      return policy;
    }
  } catch (e) {
    console.warn(LOG, 'fetch_policy_failed', e);
  }
  return {
    minIosVersion: FALLBACK_MIN_NATIVE_IOS.version,
    minIosBuild: FALLBACK_MIN_NATIVE_IOS.build,
    minAndroidVersion: null,
    minAndroidBuild: null,
    message: null,
  };
}

/**
 * @param {{ version: string, build: string }} installed
 * @param {NativeUpdatePolicy} policy
 * @param {'ios'|'android'} platform
 * @returns {boolean}
 */
export function isNativeUpdateRequired(installed, policy, platform) {
  const minVersion =
    platform === 'ios' ? policy.minIosVersion : policy.minAndroidVersion;
  const minBuild = platform === 'ios' ? policy.minIosBuild : policy.minAndroidBuild;

  if (!minVersion) return false;

  if (isRemoteVersionNewer(minVersion, installed.version)) {
    return true;
  }

  if (isRemoteVersionNewer(installed.version, minVersion)) {
    return false;
  }

  if (minBuild != null && minBuild > 0) {
    const installedBuild = parseBuildNumber(installed.build);
    if (installedBuild != null && installedBuild < minBuild) {
      return true;
    }
  }

  return false;
}

/**
 * @returns {Promise<{ required: boolean, message: string|null, installedVersion: string|null, installedBuild: string|null }>}
 */
export async function evaluateNativeUpdateGate() {
  if (!isNative()) {
    return { required: false, message: null, installedVersion: null, installedBuild: null };
  }

  const platform = getPlatform();
  if (platform !== 'ios' && platform !== 'android') {
    return { required: false, message: null, installedVersion: null, installedBuild: null };
  }

  let info;
  try {
    info = await App.getInfo();
  } catch (e) {
    console.warn(LOG, 'App.getInfo failed', e);
    return { required: false, message: null, installedVersion: null, installedBuild: null };
  }

  const installed = {
    version: String(info.version || '').trim(),
    build: String(info.build || '').trim(),
  };

  const policy = await fetchNativeUpdatePolicy();
  const required = isNativeUpdateRequired(installed, policy, platform);

  console.info(LOG, 'evaluate', {
    platform,
    installed,
    policy,
    required,
  });

  const defaultMessage =
    platform === 'ios'
      ? 'A new version of Country Chicken Co is required. Please update from the App Store to continue.'
      : 'A new version of Country Chicken Co is required. Please update from the Play Store to continue.';

  return {
    required,
    message: policy.message || defaultMessage,
    installedVersion: installed.version,
    installedBuild: installed.build,
  };
}
